import { LoadingSection } from '@/components/shared/LoadingSection';
import { Skeleton } from '@/components/ui/skeleton';
import { PAGE_SIZE } from '../hooks/use-claims-view';
import { COLUMNS } from '../utils/claim-columns';

/**
 * A full page of rows, so the tallest grid the section can draw is the one reserved. A policy with
 * fewer claims still settles shorter — the skeleton cannot know the count before the response, and
 * reserving the maximum is the choice that never leaves content hidden below the fold.
 */
const BODY_ROWS = PAGE_SIZE;

/**
 * Cells per row. The real header brackets {@link COLUMNS} with an expand toggle and a Reason column;
 * the toggle is drawn separately because it is the one fixed-width cell, so only Reason is added here.
 * Deriving the count keeps the placeholder in step with the column set instead of hardcoding it.
 */
const DATA_CELLS = COLUMNS.length + 1;

/** Placeholders for the keyword search and the Filter / Reset pair that sit on the heading row. */
function ToolbarPlaceholder(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Skeleton className="h-9 w-48 rounded-md" />
      <Skeleton className="h-8 w-20 rounded-md" />
      <Skeleton className="h-8 w-20 rounded-md" />
    </div>
  );
}

function GridRow({ header = false }: { readonly header?: boolean }): React.JSX.Element {
  return (
    <div
      className={`border-line flex items-center gap-3 border-b px-2 py-2 ${
        header ? 'bg-surface-muted' : ''
      }`}
    >
      {/* Matches the grid's fixed-width expand-toggle column, which carries no visible content. */}
      <span className="w-8 flex-none" />
      {Array.from({ length: DATA_CELLS }, (_, cell) => (
        <Skeleton key={cell} className={header ? 'h-3 flex-1' : 'h-4 flex-1'} />
      ))}
    </div>
  );
}

/**
 * The claims table while it loads. It reproduces the grid's horizontal scroll container and minimum
 * width, so the section occupies the space the real table will and the page does not jump when the
 * rows arrive.
 */
export function ClaimsSkeleton(): React.JSX.Element {
  return (
    <LoadingSection title="Claims" message="Loading claims…" headerExtra={<ToolbarPlaceholder />}>
      <div className="overflow-x-auto">
        <div className="min-w-[1100px]">
          <GridRow header />
          {Array.from({ length: BODY_ROWS }, (_, row) => (
            <GridRow key={row} />
          ))}
        </div>
      </div>
    </LoadingSection>
  );
}
